import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  motion,
  useScroll,
  useTransform,
  animate,
  useInView,
  useMotionValue,
  useSpring,
} from 'framer-motion';
import {
  ArrowDownRight,
  ArrowUpRight,
  GraduationCap,
  Heart,
  Sparkle,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  initiativePartners,
  scholarshipSources,
  kpiGroups,
} from '@/data/mock/initiative';
import academiesLogo from '@/assets/logo.png';

const HUB_LIGHT = '/logos/champions-hub-horizontal-light.png';
const HUB_DARK = '/logos/champions-hub-horizontal-dark.png';

// Animated counter that runs on first viewport entry
function Counter({ value, suffix = '' }: { value: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-15%' });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.6,
      ease: [0.2, 0.8, 0.2, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);

  return (
    <span ref={ref} className="num-tabular">
      {display}
      {suffix}
    </span>
  );
}

const MARQUEE_ITEMS_EN = [
  '150 Scholarships',
  '5 Barça',
  '4 Platforms',
  'Gaza · Palestine',
  'Football',
  'PRO · A · B · C · D',
  'Free of charge',
];

const MARQUEE_ITEMS_AR = [
  '150 منحة',
  '5 منح برشلونة',
  '4 منصات',
  'غزة · فلسطين',
  'كرة قدم',
  'PRO · A · B · C · D',
  'مجاناً تماماً',
];

export default function Initiative() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const lang = isRTL ? 'ar' : 'en';
  const ArrowIcon = isRTL ? ArrowDownRight : ArrowUpRight;

  // Scroll-driven parallax for the hero — multiple layers at different speeds.
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const backdropScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const backdropY = useTransform(scrollYProgress, [0, 1], ['0%', '12%']);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 1.4]);
  const numeralY = useTransform(scrollYProgress, [0, 1], ['0%', '25%']);
  const numeralOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.55]);
  const numeralScale = useTransform(scrollYProgress, [0, 1], [1, 0.92]);
  const contentY = useTransform(scrollYProgress, [0, 1], ['0%', '-8%']);
  const scrollProgress = useTransform(scrollYProgress, [0, 1], ['0%', '100%']);

  // Mouse-tracking tilt for the massive numeral. Subtle 3D effect.
  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const tiltRotateX = useTransform(tiltY, [-1, 1], [6, -6]);
  const tiltRotateY = useTransform(tiltX, [-1, 1], [-8, 8]);
  const tiltSpringX = useSpring(tiltRotateX, { stiffness: 80, damping: 12 });
  const tiltSpringY = useSpring(tiltRotateY, { stiffness: 80, damping: 12 });

  const handleHeroMouse = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    tiltX.set(((e.clientX - cx) / rect.width) * 2);
    tiltY.set(((e.clientY - cy) / rect.height) * 2);
  };
  const resetTilt = () => {
    tiltX.set(0);
    tiltY.set(0);
  };

  const marquee = isRTL ? MARQUEE_ITEMS_AR : MARQUEE_ITEMS_EN;
  const marqueeLoop = [...marquee, ...marquee];

  return (
    <div className="initiative-theme font-body-init overflow-x-hidden">
      {/* ───────── HERO (magazine-cover layout, compact) ───────── */}
      <section
        ref={heroRef}
        className="relative isolate pt-8 pb-16 lg:pb-20 overflow-hidden"
      >
        {/* Layer 1 — backdrop image with scroll-driven zoom & parallax */}
        <motion.img
          aria-hidden
          src="/img/initiative/hero-initiative.webp"
          alt=""
          style={{ scale: backdropScale, y: backdropY }}
          className="absolute inset-0 h-full w-full object-cover opacity-50 pointer-events-none"
          loading="eager"
        />
        <motion.div
          aria-hidden
          style={{ opacity: overlayOpacity }}
          className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/30 to-background/90 pointer-events-none"
        />
        <div aria-hidden className="absolute inset-0 bg-line-grid opacity-20 pointer-events-none" />

        {/* Atmospheric blobs */}
        <motion.div
          aria-hidden
          animate={{ scale: [1, 1.18, 1], opacity: [0.2, 0.45, 0.2], x: [0, 30, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -top-32 -left-32 w-[44rem] h-[44rem] bg-accent/12 rounded-full blur-3xl pointer-events-none"
        />
        <motion.div
          aria-hidden
          animate={{ scale: [1.1, 1, 1.1], opacity: [0.12, 0.3, 0.12], x: [0, -40, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut', delay: 5 }}
          className="absolute -bottom-24 -right-32 w-[40rem] h-[40rem] bg-primary-foreground/10 rounded-full blur-3xl pointer-events-none"
        />

        {/* ═══ Publication ribbon (top utility row) ═══ */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap items-center justify-between gap-3 py-3 border-y border-border/40 bg-background/30 backdrop-blur-sm rounded-sm">
              <span className="font-mono-mark text-[10px] uppercase tracking-[0.25em] text-foreground/85 font-bold">
                ★ {t('initiative.name')} — {t('initiative.slogan')}
              </span>
              <div className="flex items-center gap-4">
                <span className="hidden sm:inline-flex font-mono-mark text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                  {isRTL ? 'غزة · فلسطين' : 'GAZA · PALESTINE'}
                </span>
                <span className="hidden sm:inline-block h-3 w-px bg-border" />
                <span className="font-mono-mark text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                  № 01 / 2026
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          style={{ y: contentY }}
          className="container mx-auto px-4 relative z-10 pt-10 lg:pt-14"
        >
          {/* ═══ Launch badge — compact, inline ═══ */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.15, ease: [0.2, 0.8, 0.2, 1] }}
            className="flex justify-center mb-6"
          >
            <motion.div
              animate={{ y: [0, -2, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
              className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-accent/12 border border-accent/40 backdrop-blur-sm"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-accent" />
              </span>
              <span className="font-mono-mark text-[10px] uppercase tracking-[0.25em] text-accent font-bold">
                {t('initiative.launchLabel')} · {t('initiative.launchDateShort')}
              </span>
              <span className="hidden sm:inline-block h-2.5 w-px bg-accent/40" />
              <span className="hidden sm:inline font-editorial-italic text-[10px] text-accent/80">
                {t('initiative.launchAnniversary')}
              </span>
            </motion.div>
          </motion.div>

          {/* ═══ HEADLINE — restrained typographic scale ═══ */}
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className={cn(
              'font-display font-black uppercase text-balance text-center max-w-5xl mx-auto',
              'text-4xl sm:text-5xl lg:text-6xl xl:text-7xl',
              isRTL
                ? 'font-display-ar normal-case leading-[1.4] py-1'
                : 'leading-[0.95]'
            )}
          >
            {(() => {
              // Two lines: the lead + the punch with the accent word inline.
              const lines = isRTL
                ? [
                    { text: 'تعليم رياضي احترافي', accent: false },
                    { text: 'لمدربي غزة — مجاناً.', accent: true },
                  ]
                : [
                    { text: 'World-class coaching,', accent: false },
                    { text: 'delivered free to Gaza.', accent: true },
                  ];
              return lines.map((line, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, y: 30, filter: 'blur(8px)' }}
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                  transition={{
                    duration: 0.7,
                    delay: 0.3 + i * 0.12,
                    ease: [0.2, 0.8, 0.2, 1],
                  }}
                  className={cn(
                    'block',
                    // Arabic glyphs need vertical breathing room above + below.
                    isRTL && 'pb-1',
                    line.accent &&
                      'bg-gradient-to-r from-accent via-accent to-accent/70 bg-clip-text text-transparent'
                  )}
                >
                  {line.text}
                </motion.span>
              ));
            })()}
          </motion.h1>

          {/* ═══ Italic editorial kicker — restrained ═══ */}
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-5 font-editorial-italic text-base sm:text-lg text-foreground/75 leading-relaxed text-center max-w-2xl mx-auto"
          >
            "{t('initiative.subtitle')}"
          </motion.p>

          {/* ═══ CTAs — centered, balanced ═══ */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-7 flex flex-wrap items-center justify-center gap-3"
          >
            <Link to="/initiative/coaches">
              <motion.div
                whileHover={{ scale: 1.03, y: -2 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 400, damping: 18 }}
              >
                <Button
                  size="lg"
                  className="btn-glow gap-2 h-11 px-6 font-bold uppercase tracking-[0.18em] text-xs"
                >
                  <GraduationCap className="h-4 w-4" />
                  {t('initiative.ctaApply')}
                  <ArrowIcon className="h-3.5 w-3.5" />
                </Button>
              </motion.div>
            </Link>
            <Link
              to="/initiative/sponsor"
              className="group inline-flex items-center gap-2 h-11 px-5 rounded-md border-2 border-foreground/20 hover:border-accent text-xs font-bold uppercase tracking-[0.18em] text-foreground/85 hover:text-accent transition-colors"
            >
              <Heart className="h-3.5 w-3.5 text-accent" />
              {t('initiative.ctaSponsor')}
              <ArrowIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </motion.div>

        {/* ═══ Bottom stats data strip — compact ═══ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.95 }}
          className="relative z-10 mt-10 lg:mt-14"
        >
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 border-y border-border/40 bg-background/30 backdrop-blur-sm rounded-sm divide-x divide-border/40 rtl:divide-x-reverse">
              {[
                { num: 150, label: isRTL ? 'منحة مجانية' : 'Free scholarships', highlight: true },
                { num: 5, label: isRTL ? 'منح برشلونة' : 'Barça grants' },
                { num: 4, label: isRTL ? 'منصات شريكة' : 'Partner platforms' },
                { date: '6·6', label: isRTL ? 'يوم الانطلاق' : 'Launch day' },
              ].map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, delay: 1.05 + i * 0.06 }}
                  whileHover={{ y: -2 }}
                  className="group relative px-4 py-4 text-center"
                >
                  <div
                    className={cn(
                      'font-display font-black text-3xl lg:text-4xl leading-none num-tabular transition-colors',
                      s.highlight
                        ? 'bg-gradient-to-b from-accent via-accent to-accent/70 bg-clip-text text-transparent'
                        : 'text-foreground group-hover:text-accent'
                    )}
                  >
                    {s.date ? s.date : <Counter value={s.num} />}
                  </div>
                  <div className="mt-2 font-mono-mark text-[9px] uppercase tracking-[0.22em] text-muted-foreground">
                    {s.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Scroll progress bar */}
        <motion.div
          aria-hidden
          style={{ width: scrollProgress }}
          className="absolute bottom-0 left-0 h-px bg-gradient-to-r from-accent via-accent/80 to-transparent z-20"
        />
      </section>

      {/* ───────── MARQUEE ───────── */}
      <section className="relative py-6 bg-surface-2 text-foreground overflow-hidden border-y border-border/40">
        <div className="init-marquee">
          {marqueeLoop.map((item, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-6 px-6 font-display font-black text-2xl sm:text-3xl uppercase whitespace-nowrap"
            >
              {item}
              <span className="text-accent/70 text-xl">★</span>
            </span>
          ))}
        </div>
      </section>

      {/* ───────── VISION (Bento) ───────── */}
      <section className="relative py-24 overflow-hidden">
        <img
          aria-hidden
          src="/img/initiative/vision-dual.webp"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-40 pointer-events-none"
          loading="lazy"
        />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/40 to-background pointer-events-none" />
        <div className="container mx-auto px-4 relative">
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
            <div className="lg:col-span-4 lg:sticky lg:top-32">
              <div className="section-mark">№ 02 — {isRTL ? 'الرؤية' : 'Vision'}</div>
              <h2
                className={cn(
                  'mt-4 font-display font-black uppercase leading-[0.95] text-5xl lg:text-6xl text-balance',
                  isRTL && 'font-display-ar normal-case leading-[1.1]'
                )}
              >
                {isRTL ? 'هدف مزدوج. أثر واحد.' : 'A dual goal.\nOne impact.'}
              </h2>
              <p className="mt-5 text-foreground/70 leading-relaxed max-w-md">
                {isRTL
                  ? 'الإنسانية والاستدامة المؤسسية ليستا متناقضتين — واحدة تُغذّي الأخرى.'
                  : 'Humanitarian impact and institutional sustainability are not opposites — they fuel each other.'}
              </p>
            </div>

            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55 }}
                className="lift-on-hover relative p-7 lg:p-9 rounded-2xl bg-card border border-border overflow-hidden"
              >
                <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent">/ 01</div>
                <Heart className="mt-6 h-8 w-8 text-foreground/40" strokeWidth={1.5} />
                <h3 className="mt-6 font-display font-black uppercase text-3xl lg:text-4xl leading-tight">
                  {t('initiative.vision.humanitarianTitle')}
                </h3>
                <p className="mt-4 text-foreground/75 leading-relaxed">
                  {t('initiative.vision.humanitarianBody')}
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: 0.08 }}
                className="lift-on-hover relative p-7 lg:p-9 rounded-2xl bg-gradient-to-br from-accent to-accent/70 text-primary border-2 border-accent overflow-hidden sm:translate-y-8"
              >
                <div className="font-mono-mark text-[10px] uppercase tracking-widest text-primary/80">/ 02</div>
                <Sparkle className="mt-6 h-8 w-8 text-primary/70" strokeWidth={1.5} />
                <h3 className="mt-6 font-display font-black uppercase text-3xl lg:text-4xl leading-tight">
                  {t('initiative.vision.sustainabilityTitle')}
                </h3>
                <p className="mt-4 text-primary/85 leading-relaxed">
                  {t('initiative.vision.sustainabilityBody')}
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── INSTITUTIONAL STRUCTURE ───────── */}
      <section className="relative py-24 bg-surface-1/40 overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid pointer-events-none opacity-50" />
        <div className="container mx-auto px-4 relative">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div>
              <div className="section-mark">№ 03 — {isRTL ? 'الهيكل' : 'Structure'}</div>
              <h2
                className={cn(
                  'mt-4 font-display font-black uppercase leading-[0.95] text-5xl lg:text-6xl max-w-3xl text-balance',
                  isRTL && 'font-display-ar normal-case leading-[1.1]'
                )}
              >
                {t('initiative.structure.title')}
              </h2>
            </div>
            <p className="text-foreground/70 max-w-md">
              {t('initiative.structure.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {initiativePartners.map((p, idx) => {
              const isSupporter = p.emphasis === 'supporter';
              const isPrimary = p.emphasis === 'primary';
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.08 }}
                  className="group relative h-full"
                >
                  <div
                    className={cn(
                      'h-full p-7 rounded-2xl flex flex-col relative overflow-hidden lift-on-hover',
                      isPrimary && 'bg-primary text-primary-foreground border border-accent/30',
                      p.emphasis === 'co-host' && 'bg-card border border-border',
                      isSupporter && 'bg-background border border-dashed border-border'
                    )}
                  >
                    {isSupporter && (
                      <div className="absolute top-0 right-0 px-3 py-1 bg-accent text-primary font-mono-mark text-[10px] font-bold uppercase tracking-wider rounded-bl-2xl">
                        {isRTL ? 'هوية مستقلة' : 'Independent'}
                      </div>
                    )}

                    <div className="font-mono-mark text-[10px] uppercase tracking-widest opacity-60 mb-6">
                      / 0{idx + 1} {' / '}
                      {p.emphasis === 'primary' && t('initiative.structure.roleLead')}
                      {p.emphasis === 'co-host' && t('initiative.structure.roleCohost')}
                      {p.emphasis === 'supporter' && t('initiative.structure.roleSupporter')}
                    </div>

                    {/* Logo zone */}
                    <div className="h-20 flex items-center mb-6">
                      {p.id === 'champions-academies' && (
                        <img src={academiesLogo} alt="Champions Academies" className="h-16 w-auto" />
                      )}
                      {p.id === 'al-jalaa' && (
                        <div className="flex items-center gap-3">
                          <div className="h-14 w-14 rounded-xl bg-primary border border-accent/40 flex items-center justify-center shadow-lg">
                            <span className="font-display font-black text-2xl text-accent">JC</span>
                          </div>
                          <div className="font-mono-mark text-[10px] uppercase tracking-widest text-muted-foreground">
                            {isRTL ? 'الجلاء' : 'Al-Jalaa'}
                            <br />
                            {isRTL ? 'الرياضي' : 'Sports Club'}
                          </div>
                        </div>
                      )}
                      {p.id === 'champions-hub' && (
                        <a href="https://champions-hub.com" target="_blank" rel="noreferrer">
                          <img src={HUB_LIGHT} alt="Champions Hub" className="h-12 w-auto" />
                        </a>
                      )}
                    </div>

                    <h3 className="font-display font-black uppercase text-3xl leading-tight">
                      {p.name[lang]}
                    </h3>

                    <p className={cn(
                      'mt-4 leading-relaxed flex-1',
                      isPrimary ? 'text-primary-foreground/80' : 'text-foreground/75'
                    )}>
                      {p.role[lang]}
                    </p>

                    <div className={cn(
                      'mt-6 pt-4 border-t font-mono-mark text-[10px] uppercase tracking-widest',
                      isPrimary ? 'border-primary-foreground/20 text-primary-foreground/60' : 'border-border text-muted-foreground'
                    )}>
                      {p.scope[lang]}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-10 max-w-3xl mx-auto p-5 rounded-xl border-l-4 border-accent bg-accent/5"
            dir={isRTL ? 'rtl' : 'ltr'}
          >
            <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent mb-2">
              ※ {t('initiative.structure.noteLabel')}
            </div>
            <p className="text-sm text-foreground/85 leading-relaxed">
              {t('initiative.structure.note')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ───────── SCHOLARSHIPS ───────── */}
      <section className="relative py-24">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <div>
              <div className="section-mark">№ 04 — {isRTL ? 'المنح' : 'Scholarships'}</div>
              <h2
                className={cn(
                  'mt-4 font-display font-black uppercase leading-[0.95] text-5xl lg:text-6xl max-w-2xl text-balance',
                  isRTL && 'font-display-ar normal-case leading-[1.1]'
                )}
              >
                {t('initiative.scholarships.title')}
              </h2>
            </div>
            <p className="font-editorial-italic text-lg text-foreground/70 max-w-sm leading-relaxed">
              "{t('initiative.scholarships.subtitle')}"
            </p>
          </div>

          {/* Scholarship grid: featured Barça spans 2 cols */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
            {scholarshipSources.map((src, idx) => {
              const isHighlight = src.highlight;
              return (
                <motion.div
                  key={src.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.08 }}
                  className={cn(
                    'group relative overflow-hidden rounded-2xl lift-on-hover',
                    isHighlight
                      ? 'md:col-span-2 lg:col-span-2 lg:row-span-1 bg-gradient-to-br from-primary via-primary to-[#1a0a5a] text-primary-foreground p-8 lg:p-10 border border-accent/40'
                      : 'bg-card border border-border p-7'
                  )}
                >
                  {isHighlight && (
                    <>
                      <div className="absolute -top-20 -right-20 w-64 h-64 bg-accent/20 rounded-full blur-3xl" />
                      <div className="absolute top-5 right-5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent text-primary font-mono-mark text-[10px] font-bold uppercase tracking-wider">
                        <Sparkle className="h-3 w-3" />
                        {t('initiative.scholarships.headline')}
                      </div>
                    </>
                  )}

                  <div className="flex items-start justify-between gap-4">
                    <div className={cn(
                      'font-mono-mark text-[10px] uppercase tracking-widest',
                      isHighlight ? 'text-accent' : 'text-muted-foreground'
                    )}>
                      / 0{idx + 1}
                    </div>

                    {/* Partner logo */}
                    <div className={cn(
                      'flex items-center justify-end',
                      isHighlight ? 'h-14 lg:h-16' : 'h-10'
                    )}>
                      {src.logo ? (
                        src.website ? (
                          <a href={src.website} target="_blank" rel="noreferrer" aria-label={src.name.en}>
                            <img
                              src={src.logo}
                              alt={src.name.en}
                              className={cn(
                                'w-auto object-contain',
                                isHighlight ? 'h-12 lg:h-14' : 'h-9'
                              )}
                            />
                          </a>
                        ) : (
                          <img
                            src={src.logo}
                            alt={src.name.en}
                            className={cn(
                              'w-auto object-contain',
                              isHighlight ? 'h-12 lg:h-14' : 'h-9'
                            )}
                          />
                        )
                      ) : (
                        <span className={cn(
                          'inline-flex items-center px-2.5 py-1 rounded-md font-display font-black tracking-tight border',
                          isHighlight
                            ? 'text-accent border-accent/40 bg-accent/10 text-base lg:text-lg'
                            : 'text-foreground/80 border-border/60 bg-surface-2/50 text-sm'
                        )}>
                          {src.id === 'bihub' ? 'BiHUB' : src.name.en}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className={cn(
                    'mt-4 font-display font-black leading-[0.85] num-tabular',
                    isHighlight
                      ? 'text-[10rem] lg:text-[14rem] bg-gradient-to-b from-accent via-accent to-accent/80 bg-clip-text text-transparent drop-shadow-[0_2px_24px_rgba(255,255,255,0.18)]'
                      : 'text-7xl text-foreground'
                  )}>
                    {src.count}
                  </div>

                  <div className={cn(
                    'mt-3 font-mono-mark text-[10px] uppercase tracking-widest',
                    isHighlight ? 'text-primary-foreground/60' : 'text-muted-foreground'
                  )}>
                    {t('initiative.scholarships.grants')}
                  </div>

                  <h3 className={cn(
                    'mt-6 font-display font-black uppercase leading-tight',
                    isHighlight ? 'text-4xl lg:text-5xl' : 'text-2xl'
                  )}>
                    {src.name[lang]}
                  </h3>

                  <p className={cn(
                    'mt-3 text-sm leading-relaxed',
                    isHighlight ? 'text-primary-foreground/75 max-w-md' : 'text-foreground/70'
                  )}>
                    {src.importance[lang]}
                  </p>

                  <div className={cn(
                    'mt-5 pt-4 border-t font-mono-mark text-[10px] uppercase tracking-widest',
                    isHighlight ? 'border-primary-foreground/30 text-primary-foreground/75' : 'border-border text-muted-foreground'
                  )}>
                    via {src.platform[lang]}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Pool breakdown summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 rounded-xl border border-accent/30 bg-accent/5"
          >
            <div className="font-mono-mark text-[11px] uppercase tracking-widest text-foreground/70">
              {isRTL ? 'تركيبة المجموع الكلي' : 'Total pool composition'}
            </div>
            <div className="font-display font-black text-base sm:text-lg num-tabular text-foreground">
              5 + 30 + 30 + 85 ={' '}
              <span className="text-accent">150</span>{' '}
              <span className="font-mono-mark text-[11px] uppercase tracking-widest text-muted-foreground">
                {isRTL ? '· 1 مدرب = 1 كورس' : '· 1 coach = 1 course'}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ───────── AUDIENCE ───────── */}
      <section className="relative py-24 bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 bg-line-grid opacity-30" />
        <div className="container mx-auto px-4 relative">
          <div className="section-mark text-accent">№ 05 — {isRTL ? 'الجمهور' : 'Audience'}</div>
          <h2
            className={cn(
              'mt-4 mb-12 font-display font-black uppercase leading-[0.95] text-5xl lg:text-6xl max-w-3xl text-balance',
              isRTL && 'font-display-ar normal-case leading-[1.1]'
            )}
          >
            {t('initiative.audience.title')}
          </h2>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative p-8 rounded-2xl bg-surface-2 border-2 border-accent/50 text-foreground"
            >
              <div className="font-mono-mark text-[10px] uppercase tracking-widest mb-4 text-accent">
                {isRTL ? 'المرحلة الأولى — الآن' : 'Phase 01 · Now'}
              </div>
              <h3 className="font-display font-black uppercase text-3xl lg:text-4xl mb-6 leading-tight">
                {t('initiative.audience.phase1Title')}
              </h3>
              <ul className="space-y-3">
                {(t('initiative.audience.phase1Items', { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i} className="flex gap-3 text-base">
                    <span className="font-mono-mark text-xs text-accent/70 mt-1">0{i + 1}</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="relative p-8 rounded-2xl border border-primary-foreground/20 backdrop-blur-sm md:translate-y-12"
            >
              <div className="font-mono-mark text-[10px] uppercase tracking-widest text-accent mb-4">
                {isRTL ? 'المرحلة الثانية — قادمة' : 'Phase 02 · Soon'}
              </div>
              <h3 className="font-display font-black uppercase text-3xl lg:text-4xl mb-6 leading-tight">
                {t('initiative.audience.phase2Title')}
              </h3>
              <ul className="space-y-3">
                {(t('initiative.audience.phase2Items', { returnObjects: true }) as string[]).map((item, i) => (
                  <li key={i} className="flex gap-3 text-base text-primary-foreground/85">
                    <span className="font-mono-mark text-xs opacity-50 mt-1">0{i + 1}</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ───────── KPI ───────── */}
      <section className="relative py-24">
        <div className="container mx-auto px-4">
          <div className="section-mark">№ 06 — {isRTL ? 'النتائج' : 'Outcomes'}</div>
          <h2
            className={cn(
              'mt-4 mb-14 font-display font-black uppercase leading-[0.95] text-5xl lg:text-6xl max-w-3xl text-balance',
              isRTL && 'font-display-ar normal-case leading-[1.1]'
            )}
          >
            {t('initiative.kpis.title')}
          </h2>

          <div className="grid md:grid-cols-3 gap-px bg-border rounded-2xl overflow-hidden">
            {kpiGroups.map((group, idx) => (
              <motion.div
                key={group.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="bg-background p-7 lg:p-9"
              >
                <div className="flex items-baseline gap-3 mb-5">
                  <span className="font-display font-black text-6xl lg:text-7xl text-accent leading-none num-tabular">
                    0{idx + 1}
                  </span>
                  <span className="font-mono-mark text-[10px] uppercase tracking-widest text-muted-foreground">
                    /03
                  </span>
                </div>
                <h3 className="font-display font-black uppercase text-2xl lg:text-3xl mb-5">
                  {group.title[lang]}
                </h3>
                <ul className="space-y-2.5">
                  {group.items.map((item, i) => (
                    <li key={i} className="flex gap-2.5 text-sm text-foreground/80">
                      <span className="text-accent mt-1">→</span>
                      <span className="leading-relaxed">{item[lang]}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────── CLOSING CTA ───────── */}
      <section className="relative py-32 bg-surface-1 text-foreground overflow-hidden">
        <img
          aria-hidden
          src="/img/initiative/closing-cta.webp"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-55 pointer-events-none"
          loading="lazy"
        />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-surface-1/85 via-surface-1/70 to-surface-1/90 pointer-events-none" />
        <div className="absolute inset-0 bg-line-grid opacity-30" />
        <motion.div
          aria-hidden
          animate={{ rotate: 360 }}
          transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
          className="absolute -top-40 -right-40 w-[40rem] h-[40rem] border-2 border-accent/10 rounded-full"
        />

        <div className="container mx-auto px-4 relative">
          <div className="section-mark text-accent">※ {isRTL ? 'انضم الآن' : 'Join now'}</div>
          <h2
            className={cn(
              'mt-6 font-display font-black uppercase leading-[0.9] text-balance',
              'text-6xl sm:text-7xl lg:text-8xl xl:text-9xl max-w-5xl',
              isRTL && 'font-display-ar normal-case leading-[1]'
            )}
          >
            {t('initiative.finalCta.title')}
          </h2>
          <p className="mt-8 font-editorial-italic text-2xl lg:text-3xl text-foreground/75 max-w-2xl leading-snug">
            "{t('initiative.finalCta.subtitle')}"
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <Link to="/initiative/coaches">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 h-14 px-7 font-display font-black uppercase text-base tracking-widest gap-3 btn-glow"
              >
                <GraduationCap className="h-5 w-5" />
                {t('initiative.ctaApply')}
                <ArrowIcon className="h-4 w-4" />
              </Button>
            </Link>
            <Link to="/initiative/sponsor">
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent border-2 border-foreground/30 text-foreground hover:bg-foreground/5 hover:border-accent h-14 px-7 font-display font-black uppercase text-base tracking-widest gap-3"
              >
                <Heart className="h-5 w-5" />
                {t('initiative.ctaSponsor')}
              </Button>
            </Link>
          </div>

          {/* Hub credit footer-strip */}
          <div className="mt-20 pt-8 border-t border-border/40 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={academiesLogo} alt="Champions Academies" className="h-7 w-auto" />
              <span className="font-mono-mark text-[10px] uppercase tracking-widest text-foreground/65">
                {isRTL ? 'تشامبيونز أكاديمي × نادي الجلاء' : 'Champions Academies × Al-Jalaa'}
              </span>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono-mark text-[10px] uppercase tracking-widest text-foreground/60">
                {isRTL ? 'دعم تقني:' : 'Technical Support:'}
              </span>
              <a href="https://champions-hub.com" target="_blank" rel="noreferrer">
                <img src={HUB_DARK} alt="Champions Hub" className="h-6 w-auto opacity-90 hover:opacity-100 transition-opacity" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
